import { isRecord } from '../../../../utils/typeGuards';
import type {
    ElementDefinition,
    ElementLayoutMetadata,
    ElementSettings,
    ReportElementConstructor,
} from './types';

function hasOwnLayoutMetadata(ElementClass: ReportElementConstructor): boolean {
    return Object.prototype.hasOwnProperty.call(ElementClass, 'layout');
}

function validateChildDefinitionLists(
    elementType: string,
    childDefinitionLists: readonly (readonly ElementDefinition[])[]
): void {
    if (!Array.isArray(childDefinitionLists)) {
        throw new Error(
            `Element "${elementType}" layout.getChildDefinitionLists must return an array.`
        );
    }

    childDefinitionLists.forEach((definitions, listIndex) => {
        if (!Array.isArray(definitions)) {
            throw new Error(
                `Element "${elementType}" layout child list at index ${listIndex} must be an array.`
            );
        }

        definitions.forEach((definition, definitionIndex) => {
            if (
                !isRecord(definition) ||
                typeof definition.type !== 'string' ||
                !isRecord(definition.settings)
            ) {
                throw new Error(
                    `Element "${elementType}" layout child definition at ${listIndex}.${definitionIndex} is invalid.`
                );
            }
        });
    });
}

export function validateElementLayoutMetadata(ElementClass: ReportElementConstructor): void {
    if (!hasOwnLayoutMetadata(ElementClass)) {
        throw new Error(
            `Element "${ElementClass.elementType}" must define static layout metadata.`
        );
    }

    const { layout } = ElementClass;

    if (layout.flow === 'derived') {
        if (typeof layout.getChildDefinitionLists !== 'function') {
            throw new Error(
                `Element "${ElementClass.elementType}" derived layout must define getChildDefinitionLists.`
            );
        }
        return;
    }

    if ('getChildDefinitionLists' in layout) {
        throw new Error(
            `Element "${ElementClass.elementType}" ${layout.flow} layout must not define getChildDefinitionLists.`
        );
    }
}

export function getDerivedLayoutChildDefinitionLists(
    elementType: string,
    layout: ElementLayoutMetadata,
    settings: ElementSettings
): readonly (readonly ElementDefinition[])[] {
    if (layout.flow !== 'derived') {
        return [];
    }

    const childDefinitionLists = layout.getChildDefinitionLists(settings);
    validateChildDefinitionLists(elementType, childDefinitionLists);
    return childDefinitionLists;
}

export function containsFlowableElement(
    definitions: readonly ElementDefinition[],
    elementClassRegistry: ReadonlyMap<string, ReportElementConstructor>
): boolean {
    return definitions.some((definition) => {
        const ElementClass = elementClassRegistry.get(definition.type);
        if (!ElementClass) {
            throw new Error(`Unsupported element type "${definition.type}".`);
        }

        const { layout } = ElementClass;
        if (layout.flow === 'flow') {
            return true;
        }
        if (layout.flow === 'fixed') {
            return false;
        }

        return getDerivedLayoutChildDefinitionLists(
            ElementClass.elementType,
            layout,
            definition.settings
        ).some((childDefinitions) =>
            containsFlowableElement(childDefinitions, elementClassRegistry)
        );
    });
}
